import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Button, TrashFilledIcon, Typography } from '@blade/ui-kit'
import { useFormatDuration } from '@root/shared'

import { SessionDeviceIcon } from '../../common/session-device-icon'

import type { Session } from '@root/entities/auth'

type Props = {
  sessionInfo: Session.Session
  onDelete?: (sessionInfo: Session.Session) => void
}

export const SessionCard: React.FC<Props> = ({ sessionInfo, onDelete }) => {
  const { t } = useTranslation()
  const { formatDuration } = useFormatDuration()

  const renderLastSession = () => {
    if (sessionInfo.current) {
      return (
        <CurrentContainer>
          <CurrentIndicator />
          <span>{t('sessions.this_device')}</span>
        </CurrentContainer>
      )
    }

    const timeAgo = formatDuration({
      startAt: sessionInfo.lastLogin,
      mode: 'singleUnitShort',
      withAgo: true,
    })

    return (
      <div
        css={css`
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
        `}
      >
        <div>
          <Label>{t('sessions.last_session')}</Label>
          <Value>{timeAgo}</Value>
        </div>
        <AdHocButton
          variant="secondary"
          size="xs"
          color="error"
          iconLeft={<TrashFilledIcon />}
          onClick={() => {
            onDelete?.(sessionInfo)
          }}
        />
      </div>
    )
  }

  return (
    <Root>
      <TopSection>
        <DeviceContainer>
          <SessionDeviceIcon deviceType={sessionInfo.deviceType} />
          <div>
            <Label>{t('sessions.os')}</Label>
            <Value ellipsis>{sessionInfo.os ?? t('common.n_a')}</Value>
          </div>
        </DeviceContainer>
        {renderLastSession()}
      </TopSection>
      <Section>
        <div>
          <Label>{t('sessions.browser')}</Label>
          <Value ellipsis>{sessionInfo.browser ?? t('common.n_a')}</Value>
        </div>
        <div>
          <Label>{t('sessions.ip_address')}</Label>
          <Value ellipsis>{sessionInfo.ip}</Value>
        </div>
      </Section>
    </Root>
  )
}

const Root = styled.div`
  border-radius: 12px;
  background: var(--backgroundA-3);

  display: grid;
`

const Section = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  align-items: center;
  padding-inline: 16px;
  padding-block: 16px 12px;
`

const TopSection = styled(Section)`
  padding-block: 12px 16px;
  border-bottom: 1px solid var(--backgroundA-3);
`

const Label = styled(Typography).attrs({
  variant: 'body4-semi-bold',
  component: 'div',
})`
  color: var(--text-10);
  margin-bottom: 2px;
`

const DeviceContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 12px;
`

const Value = styled(Typography).attrs({
  variant: 'body2-semi-bold',
  component: 'div',
})`
  color: var(--text-12);
`

const CurrentIndicator = styled.div`
  width: 6px;
  height: 6px;
  background: var(--success-9);
  border-radius: 50%;
`

const CurrentContainer = styled(Typography).attrs({
  variant: 'body2-medium',
  component: 'div',
})`
  display: flex;
  align-items: center;
  gap: 4px;

  color: var(--text-12);
`

const AdHocButton = styled(Button)`
  border-color: var(--ruby-9);
  color: var(--ruby-9);
  &:hover {
    background-color: var(--rubyA-3);
    color: var(--ruby-9);
  }
`
